import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import { JSONSchemaObject } from '@/services/agent/instruments/InstrumentTypes';

export interface LicenseAnalysis {
    licenseType: 'Royalty-Free' | 'Rights-Managed' | 'Public Domain' | 'Unknown';
    requiresAttribution: boolean;
    canMonetize: boolean;
    termsSummary: string;
    platformName?: string;
}

export class LicenseScannerService {

    /**
     * Scans a URL for license terms using Intelligence.
     * 1. Fetches HTML content via Main process (bypass CORS).
     * 2. Sends content to Gemini for analysis.
     */
    async scanUrl(url: string): Promise<LicenseAnalysis> {
        try {
            // 1. Fetch Content via Browser Agent (Handles JS/SPA)
            if (!window.electronAPI?.agent?.extractWebPage) {
                throw new Error('Agent API not available. Are you in the Electron app?');
            }

            const response = await window.electronAPI.agent.extractWebPage(url);

            if (!response.success || !response.data?.text) {
                throw new Error(response.error || 'Failed to extract text from page');
            }

            // 2. Truncate content 
            const truncatedContent = response.data.text.substring(0, 15000);

            // 3. Autonomous Analysis
            const prompt = `
                Analyze the following webpage content (likely a music sample pack or Terms of Service page).
                Extract the license rights for using these audio samples in a commercial music release.

                URL: ${url}

                Content Snippet:
                ${truncatedContent}
                
                Return JSON only matching this schema:
                {
                    "licenseType": "Royalty-Free" | "Rights-Managed" | "Public Domain" | "Unknown",
                    "requiresAttribution": boolean,
                    "canMonetize": boolean,
                    "termsSummary": "One sentence summary of key restrictions or allowances.",
                    "platformName": "Name of the platform/website"
                }
            `;

            const schema = {
                type: 'object',
                properties: {
                    licenseType: { type: 'string', enum: ['Royalty-Free', 'Rights-Managed', 'Public Domain', 'Unknown'] },
                    requiresAttribution: { type: 'boolean' },
                    canMonetize: { type: 'boolean' },
                    termsSummary: { type: 'string' },
                    platformName: { type: 'string' }
                },
                required: ['licenseType', 'requiresAttribution', 'canMonetize', 'termsSummary']
            } satisfies JSONSchemaObject;
            const analysis = await AutonomousIntelligence.generateStructuredData<LicenseAnalysis>(prompt, schema as Record<string, unknown>);
            return analysis;

        } catch (_error: unknown) {
            return {
                licenseType: 'Unknown',
                requiresAttribution: false,
                canMonetize: false,
                termsSummary: 'Scan failed. Please verify manually.'
            };
        }
    }
}

export const licenseScannerService = new LicenseScannerService();
